import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { Subject, takeUntil, tap } from "rxjs";
import { Page } from "@models/page";
import { Order } from "@models/order";
import { SellerOrder, SellerOrdersFilters } from "@models/seller-order";
import { EventsManagementService } from "@services/events-management.service";
import { OrdersService } from "@services/orders.service";
import { PaymentMethodPipe } from "@shared/pipes/payment-method.pipe";
import { OrderPaymentStatusPipe } from "@shared/pipes/order-payment-status.pipe";
import {
  OrderDetailsDialogComponent,
  OrderDetailsDialogData,
} from "@shared/modals/order-details-dialog/order-details-dialog.component";
import { EventInfoResponseBody } from "@services/types/events-management-response.type";
import { DatepickerMaskDirective } from "@shared/directives/datepicker-mask/datepicker-mask.directive";
import { PaginatorModule, PaginatorState } from "primeng/paginator";
import { DatePickerModule } from "primeng/datepicker";
import { SkeletonModule } from "primeng/skeleton";
import { AppLayoutComponent } from "../../../shared/components/app-layout/app-layout.component";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { PageHeaderComponent } from "../../../shared/components/page-header/page-header.component";

@Component({
  selector: "app-seller-orders-page",
  imports: [
    CommonModule,
    FormsModule,
    AppLayoutComponent,
    ButtonComponent,
    PageHeaderComponent,
    PaymentMethodPipe,
    OrderPaymentStatusPipe,
    DatePickerModule,
    DatepickerMaskDirective,
    PaginatorModule,
    SkeletonModule,
  ],
  templateUrl: "./seller-orders-page.component.html",
  styleUrl: "./seller-orders-page.component.scss",
})
export class SellerOrdersPageComponent implements OnInit, OnDestroy {
  private readonly ordersService = inject(OrdersService);
  private readonly eventsManagementService = inject(EventsManagementService);
  private readonly dialog = inject(MatDialog);
  private readonly route = inject(ActivatedRoute);

  private readonly destroy$ = new Subject<void>();

  readonly today = this.getToday();
  readonly todayDate = this.formatDateForApi(this.today);
  isFiltersCollapsed = true;
  loading = true;
  errorMessage = "";
  orders: SellerOrder[] = [];
  events: EventInfoResponseBody[] = [];
  perPageOptions = [10, 20, 50];
  filters: Required<
    Pick<SellerOrdersFilters, "page" | "per_page" | "include_qr">
  > &
    Pick<
      SellerOrdersFilters,
      "event_id" | "initial_date" | "final_date" | "order_number"
    > = {
    event_id: undefined,
    initial_date: this.todayDate,
    final_date: this.todayDate,
    order_number: "",
    page: 1,
    per_page: 10,
    include_qr: false,
  };
  filterForm = {
    event_id: undefined as number | undefined,
    initial_date: this.today as Date | null,
    final_date: this.today as Date | null,
    order_number: "",
    include_qr: false,
  };

  meta: Page<SellerOrder>["meta"] = {
    total_count: 0,
    total_pages: 0,
    current_page: 1,
  };

  ngOnInit(): void {
    this.loadEvents();

    // Reage a cada mudança de query param (inclusive ao navegar de uma
    // notificação para outra, quando o componente é reaproveitado e o
    // ngOnInit não roda novamente).
    this.route.queryParamMap
      .pipe(
        tap((params) => {
          this.applyOrderFilter(params.get("order_id"));
          this.loadOrders();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Quando a página é aberta a partir de uma notificação de pagamento
   * confirmado (/seller-orders?order_id=...), pré-aplica o filtro do pedido
   * e remove o intervalo de datas (padrão "hoje") para garantir que o pedido
   * apareça independentemente da data de pagamento.
   */
  private applyOrderFilter(orderId: string | null): void {
    if (!orderId) {
      return;
    }

    this.filterForm.order_number = orderId;
    this.filterForm.initial_date = null;
    this.filterForm.final_date = null;

    this.filters.order_number = orderId;
    this.filters.initial_date = "";
    this.filters.final_date = "";
    this.filters.page = 1;

    this.isFiltersCollapsed = false;
  }

  get paginatorFirst(): number {
    return (this.meta.current_page - 1) * this.filters.per_page;
  }

  get hasInvalidDateRange(): boolean {
    const { initial_date, final_date } = this.filterForm;
    return (
      Boolean(initial_date) &&
      Boolean(final_date) &&
      initial_date!.getTime() > final_date!.getTime()
    );
  }

  get hasActiveFilters(): boolean {
    return (
      this.filters.event_id !== undefined ||
      this.filters.initial_date !== this.todayDate ||
      this.filters.final_date !== this.todayDate ||
      this.filters.order_number !== "" ||
      this.filters.include_qr
    );
  }

  onFilterChange(): void {
    this.applyFilters();
  }

  toggleFilters(): void {
    this.isFiltersCollapsed = !this.isFiltersCollapsed;
  }

  applyFilters(): void {
    if (this.hasInvalidDateRange) {
      return;
    }

    this.filters.event_id = this.filterForm.event_id;
    this.filters.initial_date = this.formatDateForApi(
      this.filterForm.initial_date,
    );
    this.filters.final_date = this.formatDateForApi(this.filterForm.final_date);
    this.filters.order_number = this.filterForm.order_number.trim();
    this.filters.include_qr = this.filterForm.include_qr;
    this.filters.page = 1;
    this.loadOrders();
  }

  clearFilters(): void {
    this.filterForm = {
      event_id: undefined,
      initial_date: this.getToday(),
      final_date: this.getToday(),
      order_number: "",
      include_qr: false,
    };
    this.applyFilters();
  }

  onPageChange(event: PaginatorState): void {
    this.filters.page = (event.page ?? 0) + 1;
    this.filters.per_page = event.rows ?? this.filters.per_page;
    this.loadOrders();
  }

  trackOrder(_index: number, order: SellerOrder): number {
    return order.id;
  }

  formatCustomer(order: SellerOrder): string {
    const firstTicket = order.tickets?.[0];
    if (!firstTicket) return "Sem comprador";
    return `${firstTicket.user_name} ${firstTicket.user_last_name ?? ""}`.trim();
  }

  private loadEvents(): void {
    this.eventsManagementService.listMyEventsMerged(0, 250).subscribe({
      next: (events) => {
        this.events = events;
      },
    });
  }

  private loadOrders(): void {
    this.loading = true;
    this.errorMessage = "";

    this.ordersService.getSellerOrders(this.filters).subscribe({
      next: (response) => {
        this.orders = response.data;
        this.meta = response.meta;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.orders = [];
        this.meta = {
          total_count: 0,
          total_pages: 0,
          current_page: 1,
        };
        this.errorMessage =
          "Não foi possível carregar as vendas no momento. Tente novamente.";
      },
    });
  }

  getPaymentStatusClass(status: string): string {
    if (status === "paid" || status === "succeeded") {
      return "seller-orders__status--paid";
    }
    if (
      status === "failed" ||
      status === "canceled" ||
      status === "cancelled"
    ) {
      return "seller-orders__status--failed";
    }
    return "seller-orders__status--pending";
  }

  openOrderDetails(order: SellerOrder): void {
    this.dialog.open(OrderDetailsDialogComponent, {
      width: "800px",
      maxWidth: "95vw",
      data: { order: this.toOrderDialogModel(order) } as OrderDetailsDialogData,
    });
  }

  private toOrderDialogModel(order: SellerOrder): Order {
    return {
      id: order.id,
      total_amount: String(order.total_amount),
      payment_method: order.payment_method,
      payment_status: order.payment_status,
      payment_status_display: order.payment_status_display,
      paid_at: order.paid_at,
      tickets: order.tickets.map((ticket) => ({
        id: ticket.id,
        price: String(ticket.price),
        user_name: ticket.user_name,
        user_last_name: ticket.user_last_name,
        user_cpf: ticket.user_cpf,
        user_email: ticket.user_email,
        title: ticket.title,
        description: ticket.title,
        tax: String(ticket.tax),
        event: {} as any,
        event_id: ticket.event_id,
        event_title: ticket.event_title,
        event_description: ticket.event_title,
        event_thumbnail: "",
        event_start_date: ticket.event_start_date,
        event_end_date: ticket.event_end_date,
        status: ticket.status as any,
        recipient_email: ticket.recipient_email ?? "",
        transfer_id: ticket.transfer_id ?? 0,
        transferred_to_email: ticket.transferred_to_email ?? "",
        transferred_to_name: ticket.transferred_to_name ?? "",
        transferable: ticket.transferable,
        event_location: {
          id: 0,
          location_name: "Não informado",
          city_name: "Não informado",
          state: "--",
          district: null,
          street: "Não informado",
          postal_code: "00000-000",
          number: "S/N",
          additional_information: "",
          maps_url: null,
          country: "BR",
        },
      })),
    };
  }

  private getToday(): Date {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }

  private formatDateForApi(date: Date | null): string {
    if (!date) {
      return "";
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
}
